import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CATEGORY_LABEL } from "@/mocks/products";
import { useLocalized } from "@/lib/localize";
import { useBilingual } from "@/lib/bilingual";
import { asset } from "@/lib/asset";

// Verified Forwaro contact data (from original forwaro.com / ICP record 20085124)
const FORWARO_TEL = "+86 769 8558 1169";
const FORWARO_FAX = "+86 769 8868 0628";
const FORWARO_EMAIL = "pro@forwaro.com";
const FORWARO_ADDRESS_EN = "Min Tian Industrial Zone, Shatian Town, Dongguan, Guangdong, China";
const FORWARO_ADDRESS_ZH = "廣東省東莞市沙田鎮民田工業區";

const Footer = () => {
  const { t } = useTranslation();
  const loc = useLocalized();
  const b = useBilingual();

  const whatsappHref = "/contact"; // placeholder until WhatsApp number confirmed

  return (
    <footer className="bg-stone-900 text-stone-300">
      <section id="contact" className="border-b border-stone-700">
      {/* Mobile-only condensed contact block — big tap CTAs instead of full form */}
      <div className="md:hidden">
        <div className="max-w-screen-xl mx-auto px-6 py-10">
          <p className="text-amber-500 text-xs font-semibold tracking-widest uppercase mb-2">
            {t("footer.getInTouch.kicker")}
          </p>
          <h2 className="text-2xl font-bold text-white mb-6 leading-tight">
            {b("Contact Forwaro", "聯繫 Forwaro")}
          </h2>

          <div className="grid grid-cols-3 gap-3 mb-6">
            <a
              href={`tel:${FORWARO_TEL.replace(/[^0-9+]/g, "")}`}
              className="flex flex-col items-center justify-center gap-1.5 bg-stone-800 hover:bg-stone-700 rounded-xl py-4 transition-colors"
            >
              <i className="ri-phone-line text-amber-500 text-xl" />
              <span className="text-white text-xs font-semibold">{t("footer.contact.phone")}</span>
            </a>
            <a
              href={`mailto:${FORWARO_EMAIL}`}
              className="flex flex-col items-center justify-center gap-1.5 bg-stone-800 hover:bg-stone-700 rounded-xl py-4 transition-colors"
            >
              <i className="ri-mail-line text-amber-500 text-xl" />
              <span className="text-white text-xs font-semibold">{t("footer.contact.email")}</span>
            </a>
            <a
              href={whatsappHref}
              className="flex flex-col items-center justify-center gap-1.5 bg-green-900/40 hover:bg-green-900/60 rounded-xl py-4 transition-colors"
            >
              <i className="ri-whatsapp-line text-green-400 text-xl" />
              <span className="text-white text-xs font-semibold">WhatsApp</span>
            </a>
          </div>

          <Link
            to="/contact?intent=quote"
            className="w-full bg-amber-700 hover:bg-amber-600 text-white text-sm font-bold py-3.5 rounded-full flex items-center justify-center gap-2 transition-colors mb-6"
          >
            <i className="ri-file-list-3-line" />
            {b("Send full inquiry", "送出完整詢價")}
          </Link>

          {/* Compact info block */}
          <div className="space-y-2.5 text-sm">
            <div className="flex items-center gap-2">
              <i className="ri-mail-line text-stone-500 text-base shrink-0" />
              <span className="text-stone-300">{FORWARO_EMAIL}</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-phone-line text-stone-500 text-base shrink-0" />
              <span className="text-stone-300">{FORWARO_TEL}</span>
            </div>
            <div className="flex items-start gap-2">
              <i className="ri-map-pin-line text-stone-500 text-base mt-0.5 shrink-0" />
              <span className="text-stone-400 text-xs leading-relaxed">
                {loc({ en: FORWARO_ADDRESS_EN, "zh-Hant": FORWARO_ADDRESS_ZH })}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop-only contact block */}
      <div className="hidden md:block">
        <div className="max-w-screen-xl mx-auto px-6 py-20">
          <p className="text-amber-500 text-xs font-semibold tracking-widest uppercase mb-3">
            {t("footer.getInTouch.kicker")}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-5 leading-tight whitespace-pre-line">
            {t("footer.getInTouch.heading")}
          </h2>
          <p className="text-stone-400 text-sm leading-relaxed mb-10 max-w-md">
            {t("footer.getInTouch.description")}
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl">
            {[
              { icon: "ri-mail-line", label: t("footer.contact.email"), value: FORWARO_EMAIL, href: `mailto:${FORWARO_EMAIL}` },
              { icon: "ri-phone-line", label: t("footer.contact.phone"), value: FORWARO_TEL, href: `tel:${FORWARO_TEL.replace(/[^0-9+]/g, "")}` },
              { icon: "ri-printer-line", label: t("footer.contact.fax"), value: FORWARO_FAX },
              {
                icon: "ri-map-pin-line",
                label: t("footer.contact.factory"),
                value: loc({ en: FORWARO_ADDRESS_EN, "zh-Hant": FORWARO_ADDRESS_ZH }),
              },
            ].map((item) => {
              const body = (
                <>
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-stone-800 text-amber-500 shrink-0">
                    <i className={`${item.icon} text-lg`} />
                  </div>
                  <div>
                    <p className="text-xs text-stone-500 uppercase tracking-wide">{item.label}</p>
                    <p className="text-stone-200 text-sm font-medium">{item.value}</p>
                  </div>
                </>
              );
              return item.href ? (
                <a
                  key={item.label}
                  href={item.href}
                  className="flex items-center gap-4 hover:text-amber-500 transition-colors"
                >
                  {body}
                </a>
              ) : (
                <div key={item.label} className="flex items-center gap-4">
                  {body}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      </section>

      {/* Footer Nav — data shared between mobile & desktop renderers */}
      {(() => {
        const productCats = [
          "Dining Tables",
          "Coffee Tables",
          "Console Tables",
          "Chairs",
          "Sofas",
          "TV Cabinets",
        ];
        const companyLinks = [
          { label: t("nav.about"), to: "/about" },
          { label: t("nav.factory"), to: "/factory" },
          { label: t("nav.contact"), to: "/contact" },
        ];
        const markets = ["Middle East", "Europe", "Americas", "Asia"];
        const marketZh: Record<string, string> = {
          "Middle East": "中東",
          "Europe": "歐洲",
          "Americas": "美洲",
          "Asia": "亞洲",
        };

        const BrandLockup = () => (
          <div>
            <div className="flex items-center gap-3 mb-4 text-white">
              <img
                src={asset("/brand/forwaro-mark-white.png")}
                alt=""
                aria-hidden="true"
                className="h-9 w-9 shrink-0 object-contain"
              />
              <span className="flex flex-col leading-tight whitespace-nowrap">
                <span className="text-xs font-bold" style={{ letterSpacing: "0.1em" }}>
                  FORWARO FURNITURE.
                </span>
                <span
                  className="text-[10px] text-stone-400 mt-0.5"
                  style={{ letterSpacing: "0.2em" }}
                >
                  風華國際 · 現代傢俱
                </span>
              </span>
            </div>
            <p className="text-stone-500 text-xs leading-relaxed">
              {t("footer.footerTagline")}
            </p>
          </div>
        );

        return (
          <>
            {/* Mobile — accordion pattern using native <details> */}
            <div className="md:hidden max-w-screen-xl mx-auto px-6 py-10 space-y-3">
              <BrandLockup />
              <div className="h-px bg-stone-800 my-4" />

              <details className="group border-b border-stone-800 pb-3">
                <summary className="flex items-center justify-between py-3 cursor-pointer list-none text-white text-sm font-semibold">
                  {t("footer.col.products")}
                  <i className="ri-add-line text-lg text-stone-500 group-open:rotate-45 transition-transform" />
                </summary>
                <ul className="space-y-3 py-2 pl-1">
                  {productCats.map((c) => (
                    <li key={c}>
                      <Link
                        to={`/products?category=${encodeURIComponent(c)}`}
                        className="text-stone-400 text-sm hover:text-amber-500 transition-colors cursor-pointer block py-1"
                      >
                        {loc(CATEGORY_LABEL[c])}
                      </Link>
                    </li>
                  ))}
                </ul>
              </details>

              <details className="group border-b border-stone-800 pb-3">
                <summary className="flex items-center justify-between py-3 cursor-pointer list-none text-white text-sm font-semibold">
                  {t("footer.col.company")}
                  <i className="ri-add-line text-lg text-stone-500 group-open:rotate-45 transition-transform" />
                </summary>
                <ul className="space-y-3 py-2 pl-1">
                  {companyLinks.map((item) => (
                    <li key={item.label}>
                      <Link
                        to={item.to}
                        className="text-stone-400 text-sm hover:text-amber-500 transition-colors cursor-pointer block py-1"
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </details>

              <details className="group border-b border-stone-800 pb-3">
                <summary className="flex items-center justify-between py-3 cursor-pointer list-none text-white text-sm font-semibold">
                  {t("footer.col.markets")}
                  <i className="ri-add-line text-lg text-stone-500 group-open:rotate-45 transition-transform" />
                </summary>
                <ul className="space-y-3 py-2 pl-1">
                  {markets.map((c) => (
                    <li key={c}>
                      <span className="text-stone-400 text-sm block py-1">{b(c, marketZh[c] || c)}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-stone-500 text-xs leading-relaxed px-1">
                  {t("footer.col.regional")}{" "}
                  <Link to="/contact" className="text-amber-500 hover:underline">
                    {t("nav.contact")}
                  </Link>
                </p>
              </details>
            </div>

            {/* Desktop — 4-col grid */}
            <div className="hidden md:grid max-w-screen-xl mx-auto px-6 py-14 grid-cols-4 gap-10">
              <BrandLockup />
              <div>
                <p className="text-white text-sm font-semibold mb-4">{t("footer.col.products")}</p>
                <ul className="space-y-2">
                  {productCats.map((c) => (
                    <li key={c}>
                      <Link
                        to={`/products?category=${encodeURIComponent(c)}`}
                        className="text-stone-500 text-xs hover:text-amber-500 transition-colors cursor-pointer"
                      >
                        {loc(CATEGORY_LABEL[c])}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-white text-sm font-semibold mb-4">{t("footer.col.company")}</p>
                <ul className="space-y-2">
                  {companyLinks.map((item) => (
                    <li key={item.label}>
                      <Link
                        to={item.to}
                        className="text-stone-500 text-xs hover:text-amber-500 transition-colors cursor-pointer"
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-white text-sm font-semibold mb-4">{t("footer.col.markets")}</p>
                <ul className="space-y-2">
                  {markets.map((c) => (
                    <li key={c}>
                      <span className="text-stone-500 text-xs">{b(c, marketZh[c] || c)}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-6 text-stone-600 text-xs leading-relaxed">
                  {t("footer.col.regional")}{" "}
                  <Link to="/contact" className="text-amber-500 hover:underline">
                    {t("nav.contact")}
                  </Link>
                </p>
              </div>
            </div>
          </>
        );
      })()}

      {/* Bottom Bar */}
      <div className="border-t border-stone-800">
        <div className="max-w-screen-xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-stone-600 text-xs">
            © {new Date().getFullYear()} Forwaro Furniture · 風華國際有限公司 · 備案號 20085124 · {t("footer.copyright")}
          </p>
          <div className="flex items-center gap-4">
            <a
              href={`mailto:${FORWARO_EMAIL}`}
              aria-label={loc({ en: "Email Forwaro", "zh-Hant": "寄信給 Forwaro" })}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-stone-800 hover:bg-amber-700 text-stone-400 hover:text-white transition-all duration-200 cursor-pointer"
            >
              <i className="ri-mail-line text-sm" />
            </a>
            <a
              href={`tel:${FORWARO_TEL.replace(/[^0-9+]/g, "")}`}
              aria-label={loc({ en: "Call Forwaro", "zh-Hant": "致電 Forwaro" })}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-stone-800 hover:bg-amber-700 text-stone-400 hover:text-white transition-all duration-200 cursor-pointer"
            >
              <i className="ri-phone-line text-sm" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
